// Pure blend-mode math. No DOM. Operates on RGBA8 Uint8ClampedArrays.
//
// Two families:
//
//   1. Separable modes — each channel is blended independently. We use the
//      standard Adobe per-channel formulas (PDF spec §11.3.5, ISO 32000) on
//      normalized 0..1 floats.
//
//   2. Non-separable modes (hue, saturation, color, luminosity) — operate on
//      whole RGB triplets via the HSY/luminosity helpers `setLum`/`setSat`
//      from the Adobe spec (PDF §11.3.5.3). NOT the same as HSL.
//
// All formulas assume non-premultiplied colors. The compositor lerps with
// `opacity` and the source alpha and composes onto destination using the
// Porter-Duff source-over rule on the alpha channel.

import type { BlendMode } from "../document/Layer";

// ---------------------------------------------------------------------------
// Separable per-channel math (each takes normalized 0..1 src/dst and returns
// the blended channel value, also 0..1).
// ---------------------------------------------------------------------------

const clamp01 = (x: number): number => (x < 0 ? 0 : x > 1 ? 1 : x);

export const blendNormal = (_d: number, s: number): number => s;

export const blendMultiply = (d: number, s: number): number => d * s;

export const blendScreen = (d: number, s: number): number => d + s - d * s;

export const blendDarken = (d: number, s: number): number => Math.min(d, s);

export const blendLighten = (d: number, s: number): number => Math.max(d, s);

export const blendColorBurn = (d: number, s: number): number => {
  if (d >= 1) return 1;
  if (s <= 0) return 0;
  return 1 - Math.min(1, (1 - d) / s);
};

export const blendColorDodge = (d: number, s: number): number => {
  if (d <= 0) return 0;
  if (s >= 1) return 1;
  return Math.min(1, d / (1 - s));
};

// Linear Burn = d + s - 1
export const blendLinearBurn = (d: number, s: number): number => clamp01(d + s - 1);

// Linear Dodge (Add) = d + s
export const blendLinearDodge = (d: number, s: number): number => clamp01(d + s);

export const blendOverlay = (d: number, s: number): number =>
  d < 0.5 ? 2 * d * s : 1 - 2 * (1 - d) * (1 - s);

export const blendHardLight = (d: number, s: number): number =>
  s < 0.5 ? 2 * d * s : 1 - 2 * (1 - d) * (1 - s);

// Photoshop / Adobe PDF spec §11.3.5.8 soft-light. Matches the W3C
// Compositing & Blending Level 1 spec (https://drafts.fxtf.org/compositing/),
// which is the same formula Photoshop ships — NOT the simpler Pegtop variant
// (`(1-2s)d² + 2sd`) that some software uses.
//
// Two branches:
//   - s ≤ 0.5 : d − (1 − 2s)·d·(1 − d)
//   - s > 0.5 : d + (2s − 1)·(D(d) − d),
//                 where D(d) = ((16d − 12)d + 4)·d for d ≤ 0.25,
//                              √d                  otherwise.
//
// This implementation matches Photoshop to ±1 unit at 8-bit precision
// (verified by BlendModeVerification.test.ts: #85). The comment-correction
// is the resolution of GitHub issue #85; no math change was required.
export const blendSoftLight = (d: number, s: number): number => {
  if (s <= 0.5) {
    return d - (1 - 2 * s) * d * (1 - d);
  }
  const dg = d <= 0.25 ? ((16 * d - 12) * d + 4) * d : Math.sqrt(d);
  return d + (2 * s - 1) * (dg - d);
};

// Vivid Light = color-burn(2s) if s < 0.5 else color-dodge(2s - 1)
export const blendVividLight = (d: number, s: number): number => {
  if (s < 0.5) return blendColorBurn(d, 2 * s);
  return blendColorDodge(d, 2 * s - 1);
};

// Linear Light = d + 2s - 1
export const blendLinearLight = (d: number, s: number): number => clamp01(d + 2 * s - 1);

// Pin Light = darken(2s) if s < 0.5 else lighten(2s - 1)
export const blendPinLight = (d: number, s: number): number => {
  if (s < 0.5) return Math.min(d, 2 * s);
  return Math.max(d, 2 * s - 1);
};

// Hard Mix = (vivid-light >= 0.5) ? 1 : 0
export const blendHardMix = (d: number, s: number): number =>
  blendVividLight(d, s) >= 0.5 ? 1 : 0;

export const blendDifference = (d: number, s: number): number => Math.abs(d - s);

export const blendExclusion = (d: number, s: number): number => d + s - 2 * d * s;

// Subtract = d - s
export const blendSubtract = (d: number, s: number): number => clamp01(d - s);

// Divide = d / s
export const blendDivide = (d: number, s: number): number => {
  if (s <= 0) return d > 0 ? 1 : 0;
  return clamp01(d / s);
};

type SeparableFn = (d: number, s: number) => number;

const SEPARABLE: Partial<Record<BlendMode, SeparableFn>> = {
  normal: blendNormal,
  multiply: blendMultiply,
  screen: blendScreen,
  darken: blendDarken,
  lighten: blendLighten,
  "color-burn": blendColorBurn,
  "color-dodge": blendColorDodge,
  "linear-burn": blendLinearBurn,
  "linear-dodge": blendLinearDodge,
  overlay: blendOverlay,
  "hard-light": blendHardLight,
  "soft-light": blendSoftLight,
  "vivid-light": blendVividLight,
  "linear-light": blendLinearLight,
  "pin-light": blendPinLight,
  "hard-mix": blendHardMix,
  difference: blendDifference,
  exclusion: blendExclusion,
  subtract: blendSubtract,
  divide: blendDivide,
};

// ---------------------------------------------------------------------------
// Non-separable HSY helpers (Adobe spec, PDF 1.7 §11.3.5.3).
// Luminance weights are Rec. 601: 0.3 R + 0.59 G + 0.11 B.
// ---------------------------------------------------------------------------

const LUM_R = 0.3;
const LUM_G = 0.59;
const LUM_B = 0.11;

interface RGB {
  r: number;
  g: number;
  b: number;
}

const lum = (c: RGB): number => LUM_R * c.r + LUM_G * c.g + LUM_B * c.b;

const clipColor = (c: RGB): RGB => {
  const l = lum(c);
  const n = Math.min(c.r, c.g, c.b);
  const x = Math.max(c.r, c.g, c.b);
  let { r, g, b } = c;
  if (n < 0) {
    r = l + ((r - l) * l) / (l - n);
    g = l + ((g - l) * l) / (l - n);
    b = l + ((b - l) * l) / (l - n);
  }
  if (x > 1) {
    r = l + ((r - l) * (1 - l)) / (x - l);
    g = l + ((g - l) * (1 - l)) / (x - l);
    b = l + ((b - l) * (1 - l)) / (x - l);
  }
  return { r, g, b };
};

const setLum = (c: RGB, l: number): RGB => {
  const d = l - lum(c);
  return clipColor({ r: c.r + d, g: c.g + d, b: c.b + d });
};

const sat = (c: RGB): number =>
  Math.max(c.r, c.g, c.b) - Math.min(c.r, c.g, c.b);

// Adobe setSat preserves the channel order of the source while remapping
// (min, mid, max) to (0, scaled, s).
const setSat = (c: RGB, s: number): RGB => {
  // Find indices of min, mid, max
  const arr: [number, "r" | "g" | "b"][] = [
    [c.r, "r"],
    [c.g, "g"],
    [c.b, "b"],
  ];
  arr.sort((a, b) => a[0] - b[0]);
  const out: RGB = { r: 0, g: 0, b: 0 };
  const lo = arr[0]!;
  const mid = arr[1]!;
  const hi = arr[2]!;
  if (hi[0] > lo[0]) {
    out[mid[1]] = ((mid[0] - lo[0]) * s) / (hi[0] - lo[0]);
    out[hi[1]] = s;
  } else {
    out[mid[1]] = 0;
    out[hi[1]] = 0;
  }
  out[lo[1]] = 0;
  return out;
};

// Returns the blended RGB for a non-separable mode.
const blendNonSeparable = (
  mode: "hue" | "saturation" | "color" | "luminosity",
  d: RGB,
  s: RGB,
): RGB => {
  switch (mode) {
    case "hue":
      // source hue, dest saturation + luminosity
      return setLum(setSat(s, sat(d)), lum(d));
    case "saturation":
      // dest hue, source saturation, dest luminosity
      return setLum(setSat(d, sat(s)), lum(d));
    case "color":
      // source hue + saturation, dest luminosity
      return setLum(s, lum(d));
    case "luminosity":
      // dest hue + saturation, source luminosity
      return setLum(d, lum(s));
  }
};

// ---------------------------------------------------------------------------
// Darker Color / Lighter Color — compare by luminance, take the whole pixel
// of the winner. (NOT per-channel.)
// ---------------------------------------------------------------------------

const blendDarkerColor = (d: RGB, s: RGB): RGB => (lum(s) < lum(d) ? s : d);
const blendLighterColor = (d: RGB, s: RGB): RGB => (lum(s) > lum(d) ? s : d);

// ---------------------------------------------------------------------------
// Dissolve — stochastic. Each output pixel is either dst or src based on a
// random threshold against the effective source alpha. Deterministic per call
// is fine here since the renderer drives full-frame redraws.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Pixel-wise compositor.
//
// `dst` and `src` are RGBA8 buffers of the same length (same w*h*4).
// `opacity` ∈ [0,1] multiplies the source alpha.
// Writes the result back into `dst`.
// ---------------------------------------------------------------------------

export interface CompositeOptions {
  /** Optional deterministic RNG for dissolve (defaults to Math.random). */
  rng?: () => number;
}

export function compositeLayer(
  dst: Uint8ClampedArray,
  src: Uint8ClampedArray,
  mode: BlendMode,
  opacity: number,
  options: CompositeOptions = {},
): void {
  if (dst.length !== src.length) {
    throw new Error("compositeLayer: dst/src length mismatch");
  }
  const op = clamp01(opacity);
  if (op === 0) return;

  const rng = options.rng ?? Math.random;
  const sep = SEPARABLE[mode];
  const isNonSep =
    mode === "hue" ||
    mode === "saturation" ||
    mode === "color" ||
    mode === "luminosity";

  for (let i = 0; i < dst.length; i += 4) {
    const sr = src[i]! / 255;
    const sg = src[i + 1]! / 255;
    const sb = src[i + 2]! / 255;
    const sa = (src[i + 3]! / 255) * op;
    if (sa === 0) continue;

    const dr = dst[i]! / 255;
    const dg = dst[i + 1]! / 255;
    const db = dst[i + 2]! / 255;
    const da = dst[i + 3]! / 255;

    // Dissolve is special: it picks src or dst per pixel and writes through.
    if (mode === "dissolve") {
      if (rng() < sa) {
        dst[i] = src[i]!;
        dst[i + 1] = src[i + 1]!;
        dst[i + 2] = src[i + 2]!;
        dst[i + 3] = 255;
      }
      continue;
    }

    // Compute the blended RGB (B) given pre-alpha src/dst RGB.
    let br: number;
    let bg: number;
    let bb: number;

    if (sep) {
      br = sep(dr, sr);
      bg = sep(dg, sg);
      bb = sep(db, sb);
    } else if (isNonSep) {
      const blended = blendNonSeparable(mode, { r: dr, g: dg, b: db }, { r: sr, g: sg, b: sb });
      br = blended.r;
      bg = blended.g;
      bb = blended.b;
    } else if (mode === "darker-color") {
      const blended = blendDarkerColor({ r: dr, g: dg, b: db }, { r: sr, g: sg, b: sb });
      br = blended.r;
      bg = blended.g;
      bb = blended.b;
    } else if (mode === "lighter-color") {
      const blended = blendLighterColor({ r: dr, g: dg, b: db }, { r: sr, g: sg, b: sb });
      br = blended.r;
      bg = blended.g;
      bb = blended.b;
    } else {
      // Unknown mode → fall back to normal.
      br = sr;
      bg = sg;
      bb = sb;
    }

    // Porter-Duff source-over on alpha; the color contributed by src is the
    // Adobe spec's [(1 - da) * src + da * blended] mixed with the dst via
    // source-over.
    const outA = sa + da * (1 - sa);
    if (outA === 0) {
      dst[i] = 0;
      dst[i + 1] = 0;
      dst[i + 2] = 0;
      dst[i + 3] = 0;
      continue;
    }

    // Effective source color contribution when dst already has some alpha.
    const cr = (1 - da) * sr + da * br;
    const cg = (1 - da) * sg + da * bg;
    const cb = (1 - da) * sb + da * bb;

    const outR = (sa * cr + (1 - sa) * da * dr) / outA;
    const outG = (sa * cg + (1 - sa) * da * dg) / outA;
    const outB = (sa * cb + (1 - sa) * da * db) / outA;

    dst[i] = Math.round(clamp01(outR) * 255);
    dst[i + 1] = Math.round(clamp01(outG) * 255);
    dst[i + 2] = Math.round(clamp01(outB) * 255);
    dst[i + 3] = Math.round(clamp01(outA) * 255);
  }
}

// ---------------------------------------------------------------------------
// Convenience: tells the renderer whether a mode is implementable with
// `ctx.globalCompositeOperation` natively or needs offscreen pixel compositing.
//
// Returns the CSS comp-op name when native, or null when we must fall back to
// `compositeLayer`.
// ---------------------------------------------------------------------------

export function nativeCompositeOp(mode: BlendMode): GlobalCompositeOperation | null {
  switch (mode) {
    case "normal":
      return "source-over";
    case "pass":
      // Pass-through: group blends directly against parent (no isolation).
      return "source-over";
    case "multiply":
      return "multiply";
    case "screen":
      return "screen";
    case "overlay":
      return "overlay";
    case "darken":
      return "darken";
    case "lighten":
      return "lighten";
    case "color-dodge":
      return "color-dodge";
    case "color-burn":
      return "color-burn";
    case "hard-light":
      return "hard-light";
    case "soft-light":
      return "soft-light";
    case "difference":
      return "difference";
    case "exclusion":
      return "exclusion";
    case "hue":
      return "hue";
    case "saturation":
      return "saturation";
    case "color":
      return "color";
    case "luminosity":
      return "luminosity";
    case "linear-dodge":
      // Photoshop's Linear Dodge (Add) maps to Canvas2D's "lighter".
      return "lighter";
    // Modes Canvas2D does NOT support natively:
    case "dissolve":
    case "linear-burn":
    case "darker-color":
    case "lighter-color":
    case "vivid-light":
    case "linear-light":
    case "pin-light":
    case "hard-mix":
    case "subtract":
    case "divide":
      return null;
  }
}
